import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dither from './Dither';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

export default function Contact() {
  const { language } = useLanguage();
  const t = translations[language];
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [focusedField, setFocusedField] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('preset') === 'chatlogs') {
        const text = "Hello Moratadeo,\n\nI would like to become a client and request authorization to enable persistent chat logs for my ISHO account.";
        setForm(prev => ({ ...prev, message: text }));
        
        // Auto-scroll to contact section and focus
        setTimeout(() => {
            const el = document.getElementById('contact');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setFocusedField('message');
        }, 400);
    }
  }, [location]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    
    setStatus('loading');
    try {
      await addDoc(collection(db, "messages"), {
        name: form.name,
        email: form.email,
        message: form.message,
        read: false,
        timestamp: serverTimestamp(),
      });
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => {
        setStatus('idle');
        navigate('/pricing-chat');
      }, 1500);
    } catch (error) {
      console.error(error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const isFieldActive = (name) => focusedField === name || form[name].length > 0;

  return (
    <section id="contact" className="w-full h-full bg-background flex flex-col p-8 md:p-10 border-b-brut justify-between min-h-[600px] relative overflow-hidden z-0">
      
      {/* Rectangular Dither Block */}
      <div className="absolute bottom-0 right-0 w-[90vw] h-[60vh] md:w-[60vw] md:h-[60vh] rounded-tl-[100px] overflow-hidden z-0 opacity-80 border-t border-l border-white/5 shadow-[-20px_-20px_60px_rgba(56,189,248,0.1)] pointer-events-auto mix-blend-screen transition-all duration-700">
          <Dither
              waveColor={[56/255, 189/255, 248/255]} 
              disableAnimation={false}
              enableMouseInteraction
              mouseRadius={0.3}
              colorNum={4}
              waveAmplitude={0.3}
              waveFrequency={3}
              waveSpeed={0.05}
          />
      </div>

      {/* Top Graphic Logo Block */}
      <div className="flex flex-col gap-4 items-start relative z-10 max-w-2xl">
            <h2 className="font-heading text-[clamp(2rem,4vw,4.5rem)] tracking-tighter text-foreground leading-[0.95] md:leading-[0.85] uppercase break-words">
                {t.contact_heading}
            </h2>
            <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-mono text-xs text-accent uppercase tracking-widest">{t.contact_lets_talk}</span>
            </div>
      </div>

      {/* Contact Form — Improved UX */}
      <div className="flex flex-col gap-6 mt-12 md:mt-0 max-w-md w-full relative z-10 bg-black/60 backdrop-blur-2xl p-6 md:p-8 rounded-2xl border border-white/10 shadow-xl">

          <form onSubmit={handleSubmit} className="flex flex-col gap-10 mt-2">
              
              {/* Name Field */}
              <div className="relative group">
                  <label className={`absolute left-0 font-mono font-bold uppercase tracking-[0.2em] transition-all duration-300 pointer-events-none ${
                      isFieldActive('name') ? 'text-accent -top-5 text-[10px]' : 'text-muted top-5 text-xs md:text-sm'
                  }`}>
                      {t.contact_your_name}
                  </label>
                  <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="bg-transparent w-full text-foreground font-heading text-xl md:text-2xl font-extrabold uppercase tracking-tighter focus:outline-none border-b-2 border-foreground/30 pb-3 pt-5 hover:border-foreground/60 focus:border-b-4 focus:border-accent transition-all focus:bg-white/5"
                  />
              </div>

              {/* Email Field */}
              <div className="relative group mt-2">
                  <label className={`absolute left-0 font-mono font-bold uppercase tracking-[0.2em] transition-all duration-300 pointer-events-none ${
                      isFieldActive('email') ? 'text-accent -top-5 text-[10px]' : 'text-muted top-5 text-xs md:text-sm'
                  }`}>
                      {t.contact_your_email}
                  </label>
                  <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="bg-transparent w-full text-foreground font-heading text-xl md:text-2xl font-extrabold uppercase tracking-tighter focus:outline-none border-b-2 border-foreground/30 pb-3 pt-5 hover:border-foreground/60 focus:border-b-4 focus:border-accent transition-all focus:bg-white/5"
                  />
              </div>

              {/* Message Field */}
              <div className="relative group mt-2">
                  <label className={`absolute left-0 font-mono font-bold uppercase tracking-[0.2em] transition-all duration-300 pointer-events-none ${
                      isFieldActive('message') ? 'text-accent -top-5 text-[10px]' : 'text-muted top-5 text-xs md:text-sm'
                  }`}>
                      {t.contact_your_message}
                  </label>
                  <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      required
                      rows={5}
                      className="bg-transparent w-full text-foreground font-heading text-lg md:text-xl font-extrabold uppercase tracking-tighter focus:outline-none border-b-2 border-foreground/30 pb-3 pt-5 hover:border-foreground/60 focus:border-b-4 focus:border-accent transition-all focus:bg-white/5 resize-none leading-relaxed"
                  />
              </div>
              
              {/* Submit Row */}
              <div className="flex items-center justify-between mt-4">
                  <motion.button 
                    type="submit"
                    disabled={status === 'loading'}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest px-6 py-3 border border-accent text-accent rounded-full hover:bg-accent hover:text-background transition-all disabled:opacity-50 cursor-pointer"
                  >
                      <span>{status === 'loading' ? t.contact_btn_sending_upper : t.contact_btn_send_upper}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                  </motion.button>
                  
                  {/* Status Indicators */}
                  <AnimatePresence>
                      {status === 'success' && (
                          <motion.div 
                              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                              className="flex items-center gap-2"
                          >
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-500 font-bold">{t.contact_success_upper}</span>
                          </motion.div>
                      )}
                      {status === 'error' && (
                          <motion.div 
                              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                              className="flex items-center gap-2"
                          >
                              <div className="w-2 h-2 rounded-full bg-red-400" />
                              <span className="text-[10px] font-mono uppercase tracking-widest text-red-400">{t.contact_error_upper}</span>
                          </motion.div>
                      )}
                  </AnimatePresence>
              </div>
          </form>

      </div>

    </section>
  );
}